import { fillText } from 'components/invoiceImport/invoiceImportText';

/** Fired by the delete modal of an appendix, for the section to drop its row. */
export const INVOICE_APPENDIX_DELETED = 'invoice_appendix_deleted';

// The api's limit (api useCases/invoice/appendices/appendixFile.js): checked
// here first, so that a file it would refuse is never sent.
export const APPENDIX_MAX_BYTES = 10 * 1024 * 1024;
const ACCEPTED_TYPES = ['application/pdf', 'image/jpeg', 'image/png'];
const ACCEPTED_EXTENSIONS = ['pdf', 'jpg', 'jpeg', 'png'];
export const APPENDIX_ACCEPT = [
  ...ACCEPTED_TYPES,
  ...ACCEPTED_EXTENSIONS.map((extension) => `.${extension}`),
].join(',');

/** The api's error codes the screen words, and their French texts. */
export const APPENDIX_ERROR_TEXTS = {
  APPENDIX_FILE_MISSING:
    'Aucun fichier reçu : déposez un seul fichier, non vide.',
  FILE_TOO_LARGE: 'Ce fichier dépasse 10 Mo.',
  APPENDIX_FILE_TYPE_INVALID:
    'Ce fichier n’est ni un PDF, ni un JPG, ni un PNG.',
  APPENDIX_NOT_FOUND: 'Cette annexe n’existe plus : la liste est relue.',
  APPENDIX_FILE_NOT_FOUND:
    'Le fichier de cette annexe est introuvable dans le stockage.',
  PENNYLANE_APPENDIX_FAILED:
    'Pennylane a refusé cette annexe : rien n’est gardé.',
  PENNYLANE_APPENDIX_UNCONFIRMED:
    'Pennylane n’a pas répondu : l’annexe a pu y être ajoutée. Vérifiez dans Pennylane avant de la déposer à nouveau, sinon elle y serait en double.',
  APPENDIX_NOT_UNCONFIRMED:
    'Cette annexe n’est plus à vérifier : la liste est relue.',
  INVOICE_APPENDIX_CONFIRM_FAILED: 'La confirmation a échoué, réessayez.',
  INVOICE_WITHOUT_COMPANY:
    'Cette facture n’a pas d’entreprise : elle ne prend pas d’annexe.',
  IMPORT_ALREADY_RUNNING:
    'Un import de ce mois est en cours : réessayez quand il sera terminé.',
  INVOICE_INFORMATION_SAVING:
    'Une autre facture de ce mois est en cours d’enregistrement : réessayez dans un instant.',
  PENNYLANE_INVOICE_NOT_FOUND:
    'Cette facture est introuvable dans Pennylane : l’annexe n’est pas ajoutée.',
  PENNYLANE_INVOICE_AMBIGUOUS:
    'Cette facture est en double ou archivée dans Pennylane : l’annexe n’est pas ajoutée. Dans Pennylane, n’en gardez qu’une, non archivée.',
  INVOICE_NOT_FOUND: 'Cette facture est introuvable.',
};

/** What each gesture says when the api answers with a code not worded above. */
export const APPENDIX_FAILURE_TEXTS = {
  loadFailed: 'Les annexes n’ont pas pu être chargées.',
  addFailed: 'L’ajout de l’annexe a échoué, réessayez.',
  downloadFailed: 'Le fichier n’a pas pu être téléchargé, réessayez.',
  deleteFailed: 'La suppression a échoué, réessayez.',
  confirmFailed: 'La confirmation a échoué, réessayez.',
  oneFileOnly: 'Déposez un seul fichier à la fois.',
};

const extensionOf = (name) => {
  const dot = String(name ?? '').lastIndexOf('.');
  return dot > 0 ? name.slice(dot + 1).toLowerCase() : '';
};

/**
 * The api's code a file would be refused with, or null: by its type and its
 * extension, then its size. The api reads the bytes anyway; this spares an
 * upload that cannot succeed.
 */
export const appendixFileRefusal = (file) => {
  if (
    !ACCEPTED_TYPES.includes(file?.type) ||
    !ACCEPTED_EXTENSIONS.includes(extensionOf(file?.name))
  )
    return 'APPENDIX_FILE_TYPE_INVALID';
  if (!(file.size > 0)) return 'APPENDIX_FILE_MISSING';
  if (file.size > APPENDIX_MAX_BYTES) return 'FILE_TOO_LARGE';
  return null;
};

/**
 * The text of a refusal: `appendixText` reads the `invoiceAppendices` space;
 * `failureKey` names the gesture's own text for an unknown code.
 */
export const appendixErrorText = (appendixText, code, failureKey) =>
  Object.prototype.hasOwnProperty.call(APPENDIX_ERROR_TEXTS, code)
    ? appendixText(`errors.${code}`, APPENDIX_ERROR_TEXTS[code])
    : appendixText(`errors.${failureKey}`, APPENDIX_FAILURE_TEXTS[failureKey]);

// On an upload, PENNYLANE_APPENDIX_FAILED means Pennylane refused the file; on
// the download of a Pennylane appendix, that it could not be read there.
export const PENNYLANE_FILE_ERROR_TEXTS = {
  PENNYLANE_APPENDIX_FAILED:
    'L’annexe n’a pas pu être récupérée dans Pennylane. Réessayez.',
};

/** The text of a refusal on the download of a Pennylane appendix. */
export const pennylaneFileErrorText = (appendixText, code) =>
  Object.prototype.hasOwnProperty.call(PENNYLANE_FILE_ERROR_TEXTS, code)
    ? appendixText(
        `errors.pennylaneFile.${code}`,
        PENNYLANE_FILE_ERROR_TEXTS[code],
      )
    : appendixErrorText(appendixText, code, 'downloadFailed');

/** "820 o", "12,4 Ko", "3,1 Mo", in the language's words and decimals. */
export const formatAppendixSize = (appendixText, bytes) => {
  if (!Number.isFinite(bytes) || bytes < 0) return '—';
  const number = new Intl.NumberFormat(appendixText('size.locale', 'fr-FR'), {
    maximumFractionDigits: 1,
  });
  if (bytes < 1024)
    return fillText(appendixText('size.bytes', '{size} o'), { size: bytes });
  if (bytes < 1024 * 1024)
    return fillText(appendixText('size.kilobytes', '{size} Ko'), {
      size: number.format(bytes / 1024),
    });
  return fillText(appendixText('size.megabytes', '{size} Mo'), {
    size: number.format(bytes / (1024 * 1024)),
  });
};
